import { LoadingOutlined } from '@ant-design/icons';
import { Input, Spin, Typography } from 'antd';
import { debounce } from 'lodash';
import React, { ChangeEvent, CSSProperties, FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import colors from '~/constants/colors';
import features from '~/constants/features';
import theme from '~/constants/theme';
import { usePath, useAgentMenuItem } from '~/hooks';
import { useDirection } from '~/hooks/i18nHooks';
import { useIsMobile, useIsTablet } from '~/hooks/useScreenSizes';
import {
  ExportLeadsButton,
  ImportLeadsButton,
  SelectedLeadsBulkEditButton,
} from '~/pages/sales/components';
import LeadAdd from '~/pages/sales/form/LeadAdd';
import { AgentLead } from '~/types/AgentLead';
import direction from '~/utils/direction';
import { isCallCenter, isSales } from '~/utils/getDecodedJwt';

export interface PageHeaderProps {
  heading: string;
  isFetching?: boolean;
  children?: React.ReactNode;
  sales?: boolean;
  selectedLeads?: AgentLead[];
  onBulkActionComplete?: () => void;
}

const PageHeader: FC<PageHeaderProps> = (props) => {
  const {
    heading = '???',
    isFetching,
    children,
    sales,
    selectedLeads = [],
    onBulkActionComplete,
  } = props;
  const { t } = useTranslation('agent');
  const dir = useDirection();
  const {
    changePath,
    params: { filter },
  } = usePath();

  const searchPlaceholder = t('searchPlaceholder');  
  // const queryParam = 'filterCompanyOrAddress';  
  const queryParam = 'searchValue';  
  const onSearch = (name: string) => changePath({ filter: { [queryParam]: name } });  
  const [state, setState] = useState<string>((filter && filter[queryParam]) || '');  

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setState(text);
    debouncedHandleSearch(text);
  };



  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSearch = useCallback(
    debounce((text: string) => {
      if (onSearch) onSearch(text);
    }, 500),
    [onSearch]
  );

  const isTablet = useIsTablet();
  const isMobile = useIsMobile();  

  return (
    <PageHeaderContainer className="PageHeader" dir={dir}>
      <div className="PageHeader-TopFiltersWrapper">
        {
          <div className="PageHeader-Heading">
            <Typography.Title level={3}>{heading}</Typography.Title>
            {isFetching ? (
              <FetchingIndicator style={{ fontSize: 18 }} />
            ) : (
              <div style={{ width: 30 }} />
            )}
          </div>
        }
        <div className="PageHeader-Search">
          {!isMobile && isSales() && (
            <>
              {/* <SelectedLeadsBulkEditButton */}
              {/*   selectedLeads={selectedLeads} */}
              {/*   onActionComplete={onBulkActionComplete} */}
              {/* /> */}
              <ExportLeadsButton />
              {!isCallCenter() && <ImportLeadsButton />}
              <LeadAdd />
            </>
          )}
          {!sales && (
            <SearchStack isMobile={isMobile}>              
              <Input.Search
                id="PageHeader_SearchInput"
                className="PageHeader-SearchInput"
                size="middle"
                value={state}
                placeholder={searchPlaceholder}
                onChange={handleChange}
                allowClear
              />
            </SearchStack>
          )}
          {isTablet && !isMobile && children}
        </div>
      </div>
      {!isTablet && children}
      {isMobile && (
        <div className="PageHeader-MobileSubRow">
          {features.agent.leads.enabled && <LeadAdd />}
          {children}
        </div>
      )}
    </PageHeaderContainer>
  );
};

export const FetchingIndicator = (props: { style: CSSProperties }) => {
  const { style } = props;
  const antIcon = <LoadingOutlined style={{ ...style }} spin />;
  return <Spin indicator={antIcon} />;
};

const PageHeaderContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 0;

  .PageHeader-TopFiltersWrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
    @media (max-width: ${theme.breakpoints.sm}px) {
      flex-direction: column;
    }

    .PageHeader-Heading {
      font-size: 28px;
      display: flex;
      align-items: baseline;

      ${direction.margin.right('2vw')}
      .ant-typography {
        margin: 0;
        color: ${colors.blue[1100]};
      }

      .ant-spin-spinning {
        ${direction.margin.left(12)}
      }
    }

    .PageHeader-Search {
      display: flex;
      flex: 1;
      justify-content: flex-end;

      @media (max-width: ${theme.breakpoints.sm}px) {
        width: 100%;
        justify-content: stretch;
      }

      .PageHeader-SearchInput {
        max-width: 350px;
        @media (max-width: ${theme.breakpoints.sm}px) {
          max-width: 100%;
          margin: 10px 0;
        }

        input {
          ::placeholder {
            color: ${colors.inputLabelColor};
          }
        }

        .ant-input-affix-wrapper:hover,
        button:hover {
          border-color: ${colors.inputHoverBorderColor};
        }

        .ant-input-affix-wrapper-focused,
        .ant-input-affix-wrapper:focus,
        button:focus {
          border-color: ${colors.inputHoverBorderColor};
          box-shadow: 0 0 0 2px ${colors.inputHoverBorderColor};
        }
      }
    }

    .PageHeader-Search > *:not(:last-child) {
      ${direction.margin.right(20)}
    }
  }

  .PageHeader-MobileSubRow {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const SearchStack = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: ${({ isMobile }) => (isMobile ? 'stretch' : 'center')};
  gap: ${({ isMobile }) => (isMobile ? '6px' : '12px')};
  width: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};
  margin-top: ${({ isMobile }) => (isMobile ? '12px' : '0')};

  .PageHeader-SearchInput {
    width: 100%;
    max-width: ${({ isMobile }) => (isMobile ? '100%' : '350px')};
  }
`;

export default PageHeader;
